import {action, observable, makeObservable} from 'mobx';
import {OB_Screen} from './OB_Screen';
import {formName} from '../screens/BankAccount/constants';
import {onboardingMessageStore} from './OB_Message_Store';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import texts from '../texts';
import {
    closeSession,
    getForm,
    saveRecord,
    startRecordEdit,
    updateRecordField
} from '../service/api/general';
import {getBankData} from '../service/api/bank_data';
import {
    findColumnByName,
    mapRecordByColumns,
    mapRecordValue
} from '../../../utils/proto-api-utils/utils';

class OB_BankAccount_Store extends OB_Screen {
    defaultBankData = null;
    @observable editRecordMode = false;
    @observable bankData = null;

    constructor() {
        super();

        makeObservable(this);
    }

    resetStore = () => {
        // reset store to default values
        this.setBankData(null);
    };

    @action setBankData = data => {
        this.defaultBankData = data;
        this.bankData = data;
    };

    requestBankData = async (form, cols) => {
        if (!this.defaultBankData && !this.editRecordMode) {
            const res = await getBankData(form, cols);

            return res.Records.length > 0 ? this.mapBankAccountRecord(res.Records, cols) : null;
        }

        return this.defaultBankData;
    };

    getBankAccountData = async () => {
        this.setLoading(true);

        try {
            onboardingMessageStore.clearMessage();

            const {form, cols} = await getForm(formName);
            const bankData = await this.requestBankData(form, cols);

            this.setBankData(bankData);

            if (!bankData) {
                await this.runEditAction(form.Session);
            }
            this.setLoading(false);
        } catch (e) {
            this.handleError(e);
        }
    };

    runEditAction = async (Session: string) => {
        if (!this.editRecordMode) {
            await startRecordEdit({
                Session,
                ScrLine: 0,
                Keys: null,
                formName
            });
            this.editRecordMode = true;
        }
    };

    mapBankAccountRecord = (record, cols) => mapRecordByColumns(record, cols)[0];

    fieldUpdate = async (value: string, name: string) => {
        const {
            form: {Session},
            cols
        } = await getForm(formName);

        const Column = findColumnByName(cols.Columns, name);
        const ColumnID = Column?.ID;
        const Value = mapRecordValue(value, Column?.Type);

        try {
            const res = await updateRecordField({Value, Session, ColumnID, Fname: formName});

            if (res.Record) {
                this.bankData = {
                    ...this.bankData,
                    ...this.mapBankAccountRecord([res.Record], cols)
                };
            }

            return res;
        } catch (e) {
            return this.handleError(e);
        }
    };

    saveBankAccount = async (form: any) => {
        const {
            form: {Session}
        } = await getForm(formName);

        const onFinish = async () => {
            this.setSubmitted(false);
            this.editRecordMode = false;

            await closeSession(form.Session, formName);

            showSuccessToast(texts.bank_acc_tab_success);
        };

        try {
            this.setSubmitted(true);

            await this.fieldUpdate(form.BRANCH, 'BRANCH');
            await this.fieldUpdate(form.ACCNAME, 'CASHNAME');
            await this.fieldUpdate(`${this.bankData.BANKNAME} ${form.PAYACCOUNT}`, 'CASHDES');
            await this.fieldUpdate('Y\u0000', 'MAINBANKFLAG');

            await this.fieldUpdate(form.PAYACCOUNT, 'PAYACCOUNT');
            await saveRecord(Session, onFinish);

            this.bankData = {...this.bankData, ...form};
            // call onFinish callback if there is no Message in the saveRecord response
            await onFinish();
        } catch (e) {
            this.handleError(e);
            this.setSubmitted(false);
        }
    };

    handleError = e => {
        const {message} = onboardingMessageStore;

        if (message?.message?.MType === 3) {
            this.editRecordMode = false;
        }

        this.setLoading(false);

        return;
    };
}

export const bankAccountFormStore = new OB_BankAccount_Store();
